<template>
  <main class="crm-admin">
    <section class="crm-shell">
      <nav class="tabs">
        <button class="active">환자관리 CRM</button>
      </nav>

      <div class="toolbar">
        <button @click="openCrmModal(null)">등록</button>
        <button :disabled="!selectedRecord" @click="selectedRecord && openCrmModal(selectedRecord)">편집</button>
        <button :disabled="!selectedRecord" @click="completeSelected">완료 처리</button>
        <button :disabled="!selectedRecord" @click="scheduleRecall">리콜 예약</button>
        <label class="filter">
          <span>필터</span>
          <input v-model="filterText" placeholder="환자명/차트번호/전화번호/내용 검색" />
        </label>
        <label>
          <span>업무</span>
          <select v-model="typeFilter">
            <option>전체</option>
            <option>리콜</option>
            <option>해피콜</option>
            <option>예약미이행</option>
            <option>컴플레인</option>
          </select>
        </label>
        <label>
          <span>상태</span>
          <select v-model="statusFilter">
            <option>전체</option>
            <option>대기</option>
            <option>진행중</option>
            <option>완료</option>
            <option>보류</option>
            <option>위험</option>
          </select>
        </label>
        <label>
          <span>예정일</span>
          <input v-model="dateFilter" type="date" />
        </label>
      </div>

      <section class="summary-grid">
        <article><span>리콜 대기</span><strong>{{ summary.recall }}건</strong></article>
        <article><span>해피콜</span><strong>{{ summary.happy }}건</strong></article>
        <article><span>예약 미이행</span><strong class="warn">{{ summary.missed }}건</strong></article>
        <article><span>컴플레인</span><strong class="danger">{{ summary.complaint }}건</strong></article>
      </section>

      <section class="content-grid">
        <section class="table-area">
          <table>
            <thead>
              <tr>
                <th>예정일</th>
                <th>차트번호</th>
                <th>환자</th>
                <th>전화번호</th>
                <th>업무</th>
                <th>상태</th>
                <th>중요도</th>
                <th>담당자</th>
                <th>최근 내원</th>
                <th>내용</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="record in filteredRecords"
                :key="record.id"
                :class="{ selected: selectedRecordId === record.id, risk: record.priority === '높음' || record.type === '컴플레인' }"
                @click="selectedRecordId = record.id"
                @dblclick="openCrmModal(record)"
              >
                <td>{{ record.dueDate }}</td>
                <td class="mono">{{ record.chartNo }}</td>
                <td>{{ record.patientName }}</td>
                <td>{{ record.phone || '-' }}</td>
                <td><span class="type" :class="typeClass(record.type)">{{ record.type }}</span></td>
                <td><span class="status" :class="statusClass(record.status)">{{ record.status }}</span></td>
                <td>{{ record.priority }}</td>
                <td>{{ record.owner }}</td>
                <td>{{ record.lastVisit || '-' }}</td>
                <td>{{ record.content }}</td>
              </tr>
            </tbody>
          </table>
          <footer>{{ filteredRecords.length }}개 항목</footer>
        </section>

        <aside class="side-panel">
          <section>
            <header>오늘 처리할 업무</header>
            <button v-for="record in todayTasks" :key="record.id" type="button" @click="openCrmModal(record)">
              <strong>{{ record.patientName }}</strong>
              <span>{{ record.type }} · {{ record.content }}</span>
            </button>
            <p v-if="!todayTasks.length">오늘 예정된 CRM 업무가 없습니다.</p>
          </section>

          <section>
            <header>위험/컴플레인</header>
            <button v-for="record in riskTasks" :key="record.id" type="button" @click="openCrmModal(record)">
              <strong>{{ record.patientName }}</strong>
              <span>{{ record.status }} · {{ record.content }}</span>
            </button>
            <p v-if="!riskTasks.length">위험 업무가 없습니다.</p>
          </section>
        </aside>
      </section>
    </section>

    <div v-if="crmModalOpen" class="modal-backdrop">
      <section class="modal">
        <header>
          <strong>{{ isCreating ? 'CRM 업무 등록' : `${editingRecord.patientName} CRM 상세` }}</strong>
          <button @click="closeCrmModal">×</button>
        </header>

        <nav class="modal-tabs">
          <button :class="{ active: modalTab === 'basic' }" @click="modalTab = 'basic'">기본 정보</button>
          <button :class="{ active: modalTab === 'action' }" @click="modalTab = 'action'">처리 내용</button>
          <button :class="{ active: modalTab === 'history' }" @click="modalTab = 'history'">이력</button>
        </nav>

        <div class="crm-summary">
          <div><span>환자</span><strong>{{ editingRecord.patientName || '환자 선택 필요' }}</strong></div>
          <div><span>차트번호</span><strong>{{ editingRecord.chartNo || '-' }}</strong></div>
          <div><span>업무</span><strong>{{ editingRecord.type }}</strong></div>
          <div><span>상태</span><strong>{{ editingRecord.status }}</strong></div>
        </div>

        <div v-if="modalTab === 'basic'" class="modal-body form-layout">
          <label class="patient-picker">
            <span class="field-label">환자 <em>*</em></span>
            <div>
              <input
                v-model="patientQuery"
                placeholder="이름/차트/전화 검색"
                @focus="patientListOpen = true"
                @keyup.enter="selectFirstPatient"
              />
              <div v-if="patientListOpen && patientQuery.trim()" class="patient-results">
                <button v-for="patient in filteredPatients" :key="patient.chartNo" type="button" @click="selectPatient(patient)">
                  <strong>{{ patient.name }}</strong>
                  <span>{{ patient.chartNo }} · {{ patient.phone || '-' }}</span>
                </button>
                <p v-if="!filteredPatients.length">검색 결과가 없습니다.</p>
              </div>
            </div>
          </label>
          <label><span class="field-label">업무 구분</span><select v-model="editingRecord.type"><option>리콜</option><option>해피콜</option><option>예약미이행</option><option>컴플레인</option></select></label>
          <label><span class="field-label">예정일</span><input v-model="editingRecord.dueDate" type="date" /></label>
          <label><span class="field-label">상태</span><select v-model="editingRecord.status"><option>대기</option><option>진행중</option><option>완료</option><option>보류</option><option>위험</option></select></label>
          <label><span class="field-label">중요도</span><select v-model="editingRecord.priority"><option>낮음</option><option>보통</option><option>높음</option></select></label>
          <label><span class="field-label">담당자</span><input v-model="editingRecord.owner" /></label>
          <label><span class="field-label">최근 내원일</span><input v-model="editingRecord.lastVisit" type="date" /></label>
          <label><span class="field-label">다음 예약일</span><input v-model="editingRecord.nextReservation" type="date" /></label>
          <label class="wide"><span class="field-label">업무 내용</span><input v-model="editingRecord.content" placeholder="리콜 사유, 해피콜 목적, 컴플레인 요약" /></label>
        </div>

        <div v-if="modalTab === 'action'" class="modal-body action-layout">
          <section>
            <header>처리 결과</header>
            <label><span class="field-label">연락 결과</span><select v-model="editingRecord.callResult"><option>미연락</option><option>통화완료</option><option>부재중</option><option>문자발송</option><option>예약완료</option><option>내원완료</option></select></label>
            <label><span class="field-label">상담/처리 메모</span><textarea v-model="editingRecord.memo" rows="7" /></label>
          </section>
          <section>
            <header>다음 조치</header>
            <label><span class="field-label">다음 연락일</span><input v-model="editingRecord.nextActionDate" type="date" /></label>
            <label><span class="field-label">후속 조치</span><input v-model="editingRecord.nextAction" placeholder="예약 안내, 원장 상담 연결, 환불 검토 등" /></label>
          </section>
        </div>

        <div v-if="modalTab === 'history'" class="modal-body history-layout">
          <ul>
            <li v-for="history in editingRecord.history" :key="history.id">
              <span>{{ history.time }}</span>
              <strong>{{ history.action }}</strong>
              <p>{{ history.memo }}</p>
            </li>
          </ul>
          <p v-if="!editingRecord.history.length">처리 이력이 없습니다.</p>
        </div>

        <footer>
          <button class="ghost" @click="closeCrmModal">취소</button>
          <button class="primary" @click="saveCrmRecord">저장</button>
        </footer>
      </section>
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';

type CrmType = '리콜' | '해피콜' | '예약미이행' | '컴플레인';
type CrmStatus = '대기' | '진행중' | '완료' | '보류' | '위험';
type Priority = '낮음' | '보통' | '높음';
type CallResult = '미연락' | '통화완료' | '부재중' | '문자발송' | '예약완료' | '내원완료';
type History = { id: number; time: string; action: string; memo: string };
type CrmRecord = {
  id: number;
  chartNo: string;
  patientName: string;
  phone: string;
  type: CrmType;
  status: CrmStatus;
  priority: Priority;
  dueDate: string;
  owner: string;
  lastVisit: string;
  nextReservation: string;
  content: string;
  callResult: CallResult;
  memo: string;
  nextAction: string;
  nextActionDate: string;
  history: History[];
};
type PatientOption = { id?: number; chartNo: string; name: string; phone: string; lastVisit?: string };
type Reservation = { id?: number; chartNo?: string; patient?: string; patientName?: string; phone?: string; date?: string; status?: string; treatment?: string };

const STORAGE_KEY = 'bioa-crm-records';

const records = ref<CrmRecord[]>(loadRecords());
const selectedRecordId = ref<number | null>(records.value[0]?.id ?? null);
const filterText = ref('');
const typeFilter = ref<'전체' | CrmType>('전체');
const statusFilter = ref<'전체' | CrmStatus>('전체');
const dateFilter = ref('');
const crmModalOpen = ref(false);
const isCreating = ref(false);
const modalTab = ref<'basic' | 'action' | 'history'>('basic');
const patientQuery = ref('');
const patientListOpen = ref(false);
const editingRecord = reactive<CrmRecord>(blankRecord());

const selectedRecord = computed(() => records.value.find((record) => record.id === selectedRecordId.value) || null);
const patientOptions = computed(() => loadPatientOptions());
const filteredPatients = computed(() => {
  const query = patientQuery.value.trim().toLowerCase();
  if (!query) return patientOptions.value.slice(0, 30);
  return patientOptions.value.filter((item) => [item.name, item.chartNo, item.phone].join(' ').toLowerCase().includes(query)).slice(0, 50);
});
const filteredRecords = computed(() => {
  const query = filterText.value.trim().toLowerCase();
  return records.value
    .filter((record) => typeFilter.value === '전체' || record.type === typeFilter.value)
    .filter((record) => statusFilter.value === '전체' || record.status === statusFilter.value)
    .filter((record) => !dateFilter.value || record.dueDate === dateFilter.value)
    .filter((record) => {
      if (!query) return true;
      return [record.patientName, record.chartNo, record.phone, record.type, record.content, record.memo, record.owner].join(' ').toLowerCase().includes(query);
    })
    .sort((a, b) => `${a.dueDate}-${a.id}`.localeCompare(`${b.dueDate}-${b.id}`));
});
const summary = computed(() => ({
  recall: records.value.filter((record) => record.type === '리콜' && record.status !== '완료').length,
  happy: records.value.filter((record) => record.type === '해피콜' && record.status !== '완료').length,
  missed: records.value.filter((record) => record.type === '예약미이행' && record.status !== '완료').length,
  complaint: records.value.filter((record) => record.type === '컴플레인' && record.status !== '완료').length,
}));
const todayTasks = computed(() => records.value.filter((record) => record.dueDate <= today() && record.status !== '완료').sort((a, b) => a.dueDate.localeCompare(b.dueDate)).slice(0, 8));
const riskTasks = computed(() => records.value.filter((record) => record.priority === '높음' || record.type === '컴플레인' || record.status === '위험').slice(0, 8));

function openCrmModal(record: CrmRecord | null) {
  isCreating.value = !record;
  Object.assign(editingRecord, record ? cloneRecord(record) : blankRecord());
  patientQuery.value = editingRecord.chartNo ? `${editingRecord.patientName} ${editingRecord.chartNo}` : '';
  patientListOpen.value = false;
  modalTab.value = 'basic';
  crmModalOpen.value = true;
}

function closeCrmModal() {
  crmModalOpen.value = false;
}

function saveCrmRecord() {
  if (!editingRecord.patientName.trim()) return;
  addHistory(editingRecord, '저장', `${editingRecord.type} ${editingRecord.status}`);
  const payload = cloneRecord(editingRecord);
  const index = records.value.findIndex((record) => record.id === payload.id);
  if (index >= 0) records.value[index] = payload;
  else records.value.unshift(payload);
  selectedRecordId.value = payload.id;
  persist();
  closeCrmModal();
}

function completeSelected() {
  if (!selectedRecord.value) return;
  selectedRecord.value.status = '완료';
  selectedRecord.value.callResult = selectedRecord.value.callResult === '미연락' ? '통화완료' : selectedRecord.value.callResult;
  addHistory(selectedRecord.value, '완료 처리', 'CRM 업무 완료');
  persist();
}

function scheduleRecall() {
  if (!selectedRecord.value) return;
  const source = selectedRecord.value;
  const next: CrmRecord = {
    ...cloneRecord(source),
    id: Date.now(),
    type: '리콜',
    status: '대기',
    priority: '보통',
    dueDate: addDays(30),
    content: `${source.patientName} 후속 리콜`,
    callResult: '미연락',
    nextAction: '후속 리콜',
    nextActionDate: addDays(30),
    history: [],
  };
  addHistory(next, '리콜 예약', `${source.content} 후속 관리`);
  records.value.unshift(next);
  selectedRecordId.value = next.id;
  persist();
}

function selectPatient(patient: PatientOption) {
  editingRecord.chartNo = patient.chartNo;
  editingRecord.patientName = patient.name;
  editingRecord.phone = patient.phone;
  editingRecord.lastVisit = patient.lastVisit || editingRecord.lastVisit;
  patientQuery.value = `${patient.name} ${patient.chartNo}`;
  patientListOpen.value = false;
}

function selectFirstPatient() {
  const first = filteredPatients.value[0];
  if (first) selectPatient(first);
}

function typeClass(type: CrmType) {
  return {
    recall: type === '리콜',
    happy: type === '해피콜',
    missed: type === '예약미이행',
    complaint: type === '컴플레인',
  };
}

function statusClass(status: CrmStatus) {
  return {
    waiting: status === '대기',
    working: status === '진행중',
    done: status === '완료',
    hold: status === '보류',
    risk: status === '위험',
  };
}

function addHistory(record: CrmRecord, action: string, memo: string) {
  record.history.unshift({ id: Date.now() + Math.random(), time: new Date().toISOString().slice(0, 16).replace('T', ' '), action, memo });
}

function blankRecord(): CrmRecord {
  return {
    id: Date.now(),
    chartNo: '',
    patientName: '',
    phone: '',
    type: '리콜',
    status: '대기',
    priority: '보통',
    dueDate: today(),
    owner: '상담실',
    lastVisit: '',
    nextReservation: '',
    content: '정기 리콜 안내',
    callResult: '미연락',
    memo: '',
    nextAction: '',
    nextActionDate: '',
    history: [],
  };
}

function cloneRecord(record: CrmRecord): CrmRecord {
  return JSON.parse(JSON.stringify(record));
}

function loadRecords(): CrmRecord[] {
  const stored = readStorage<CrmRecord[]>(STORAGE_KEY, []);
  if (stored.length) return stored.map(normalizeRecord);
  const seeded = seedRecords();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
  return seeded;
}

function normalizeRecord(record: Partial<CrmRecord>): CrmRecord {
  return {
    ...blankRecord(),
    ...record,
    history: record.history || [],
  };
}

function seedRecords(): CrmRecord[] {
  const missed = missedReservations().slice(0, 4).map((reservation, index) => normalizeRecord({
    id: 100 + index,
    chartNo: reservation.chartNo || `P-${1005 + index}`,
    patientName: reservation.patient || reservation.patientName || '예약환자',
    phone: reservation.phone || '',
    type: '예약미이행',
    status: '대기',
    priority: '높음',
    dueDate: today(),
    lastVisit: '',
    content: `${reservation.date || ''} ${reservation.treatment || '진료 예약'} 미이행 추적`,
    owner: '데스크',
  }));
  return missed;
}

function loadPatientOptions(): PatientOption[] {
  const patients = readStorage<Array<{ id?: number; chartNo?: string; name?: string; phone?: string }>>('bioa-patients', []);
  const visits = readStorage<Array<{ chartNo?: string; patientName?: string; phone?: string; date?: string }>>('bioa-visits', []);
  const merged = new Map<string, PatientOption>();
  patients.map((patient) => ({ id: patient.id, chartNo: patient.chartNo || '', name: patient.name || '', phone: patient.phone || '' }))
    .filter((patient) => patient.chartNo && patient.name)
    .forEach((patient) => merged.set(patient.chartNo, patient));
  visits.forEach((visit) => {
    if (!visit.chartNo) return;
    const current = merged.get(visit.chartNo);
    if (current) current.lastVisit = [current.lastVisit || '', visit.date || ''].sort().pop();
  });
  return Array.from(merged.values());
}

function missedReservations(): Reservation[] {
  return readStorage<Reservation[]>('bioa-reservations', []).filter((reservation) => ['미이행', '노쇼', '취소'].includes(reservation.status || ''));
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records.value));
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}
</script>

<style scoped>
.crm-admin{height:100%;min-height:0;overflow:hidden;background:#f6f9fb;color:#34404a;font-size:13px}.crm-shell{height:100%;min-height:0;background:#fff;border-top:1px solid #d9e2ea;display:flex;flex-direction:column;overflow:hidden}.tabs{display:flex;gap:22px;height:38px;align-items:flex-end;padding:0 20px;border-bottom:1px solid #d9e2ea;flex:0 0 auto}.tabs button{height:38px;border:0;background:transparent;color:#2275d7;font-weight:900;position:relative}.tabs button:after{content:'';position:absolute;left:8px;right:8px;bottom:-1px;height:2px;background:#2275d7}.toolbar{height:52px;display:flex;align-items:center;gap:6px;padding:8px 16px;border-bottom:1px solid #e3eaf0;box-sizing:border-box;flex:0 0 auto}.toolbar button,.modal button,.side-panel button{height:30px;border:0;border-radius:4px;background:#e8eef4;color:#3d4b57;padding:0 12px;font-weight:800;cursor:pointer}.toolbar button:disabled{opacity:.45;cursor:not-allowed}.toolbar label{display:grid;grid-template-columns:auto minmax(0,1fr);align-items:center;gap:6px;color:#7b8994}.toolbar input,.toolbar select{height:30px;border:1px solid #cfd9e3;border-radius:4px;padding:0 8px;box-sizing:border-box}.filter{margin-left:auto}.filter input{width:280px}.summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;padding:12px 16px;border-bottom:1px solid #e3eaf0;background:#fbfcfd;flex:0 0 auto}.summary-grid article{border:1px solid #dce5ec;background:#fff;border-radius:4px;padding:10px;display:grid;gap:4px}.summary-grid span{color:#7b8994}.summary-grid strong{font-size:18px;color:#263746}.summary-grid .warn{color:#b7791f}.summary-grid .danger{color:#c24135}.content-grid{min-height:0;flex:1;display:grid;grid-template-columns:minmax(0,1fr) 300px;overflow:hidden}.table-area{min-width:0;min-height:0;display:grid;grid-template-rows:minmax(0,1fr) 29px;overflow:hidden}.table-area table{width:100%;border-collapse:collapse;table-layout:fixed}.table-area thead{position:sticky;top:0;z-index:1}.table-area th,.table-area td{height:30px;border-bottom:1px solid #e5ebf0;border-right:1px solid #edf2f6;padding:0 8px;text-align:left;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.table-area th{background:#f8fafc;color:#7b8994;font-weight:800}.table-area tbody{display:block;height:100%;overflow:auto}.table-area thead,.table-area tbody tr{display:table;width:100%;table-layout:fixed}.table-area tbody tr{cursor:pointer}.table-area tbody tr:hover,.table-area tbody tr.selected{background:#eef5fb}.table-area tbody tr.risk{box-shadow:inset 3px 0 0 #e25b5b}.table-area footer{height:29px;display:flex;align-items:center;justify-content:flex-end;padding:0 16px;border-top:1px solid #d9e2ea;color:#4b5965}.mono{font-family:Consolas,monospace}.type,.status{display:inline-flex;align-items:center;height:20px;border-radius:4px;padding:0 7px;background:#edf2f7;color:#4a5568;font-weight:900;font-size:12px}.type.recall{background:#eef5fb;color:#2275d7}.type.happy{background:#e7f6ee;color:#1f7a4d}.type.missed{background:#fff4db;color:#9a6700}.type.complaint{background:#ffe7e2;color:#b43a2d}.status.waiting{background:#edf2f7;color:#4a5568}.status.working{background:#eef5fb;color:#2275d7}.status.done{background:#e7f6ee;color:#1f7a4d}.status.hold{background:#fff4db;color:#9a6700}.status.risk{background:#ffe7e2;color:#b43a2d}.side-panel{min-height:0;border-left:1px solid #e3eaf0;background:#fbfcfd;padding:12px;box-sizing:border-box;overflow:auto;display:grid;align-content:start;gap:12px}.side-panel section{border:1px solid #dce5ec;background:#fff;border-radius:4px;padding:10px;display:grid;gap:7px}.side-panel header{font-weight:900;color:#34404a}.side-panel button{height:auto;min-height:42px;background:#fff;border:1px solid #e3eaf0;text-align:left;display:grid;gap:3px}.side-panel button span{color:#7b8994;font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.side-panel p{margin:0;color:#8a97a3}.modal-backdrop{position:fixed;inset:0;background:rgba(22,34,45,.24);display:flex;align-items:center;justify-content:center;z-index:40}.modal{width:min(920px,calc(100vw - 28px));height:min(660px,calc(100vh - 28px));background:#fff;border:1px solid #cfd9e3;box-shadow:0 20px 50px rgba(31,45,58,.22);display:flex;flex-direction:column}.modal>header{height:38px;border-bottom:1px solid #d9e2ea;display:flex;align-items:center;justify-content:space-between;padding:0 18px;box-sizing:border-box}.modal>header button{width:28px;padding:0;background:transparent;color:#98a2ad;font-size:22px}.modal-tabs{height:48px;border-bottom:1px solid #d9e2ea;display:flex;align-items:flex-end;gap:24px;padding:0 26px;flex:0 0 auto}.modal-tabs button{height:38px;background:transparent;border-radius:0;padding:0 8px;color:#34404a;position:relative}.modal-tabs button.active{color:#2275d7}.modal-tabs button.active:after{content:'';position:absolute;left:0;right:0;bottom:-1px;height:2px;background:#2275d7}.crm-summary{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));border-bottom:1px solid #e3eaf0;background:#fbfcfd}.crm-summary div{padding:12px 26px;display:grid;gap:4px}.crm-summary span{color:#7b8994;font-size:12px;font-weight:800}.crm-summary strong{color:#263746}.modal-body{min-height:0;flex:1;overflow:auto;padding:18px 26px;box-sizing:border-box}.form-layout{display:grid;grid-template-columns:1fr 1fr;gap:12px 20px;align-content:start}.form-layout label,.action-layout label{display:grid;gap:6px;color:#5f6e7b}.form-layout .wide{grid-column:1/-1}.field-label{font-weight:800}.field-label em{color:#d33;font-style:normal}.form-layout input,.form-layout select,.action-layout input,.action-layout select,.action-layout textarea{width:100%;border:1px solid #cfd9e3;border-radius:4px;padding:0 9px;box-sizing:border-box}.form-layout input,.form-layout select,.action-layout input,.action-layout select{height:30px}.action-layout textarea{padding:9px;resize:vertical}.patient-picker{position:relative}.patient-results{position:absolute;top:54px;left:0;width:360px;max-height:260px;overflow:auto;border:1px solid #cfd9e3;border-radius:6px;background:#fff;box-shadow:0 14px 30px rgba(35,49,61,.16);padding:5px;box-sizing:border-box;z-index:5}.patient-results button{width:100%;height:auto;min-height:42px;background:#fff;text-align:left;display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:8px;padding:7px 9px}.patient-results button:hover{background:#f3f8fb}.patient-results span{font-size:12px;color:#7b8994}.patient-results p{margin:0;padding:12px;color:#8a97a3;text-align:center}.action-layout{display:grid;grid-template-columns:minmax(0,1fr) 280px;gap:14px}.action-layout section{border:1px solid #dce5ec;border-radius:4px;background:#fff;padding:12px;display:grid;align-content:start;gap:10px}.action-layout header{font-weight:900;color:#34404a}.history-layout ul{margin:0;padding:0;list-style:none;display:grid;gap:8px}.history-layout li{border:1px solid #e3eaf0;border-radius:4px;padding:9px;background:#fbfcfd}.history-layout span{color:#7b8994;font-size:12px}.history-layout strong{display:block;margin-top:3px;color:#263746}.history-layout p{margin:4px 0 0;color:#5f6e7b}.modal>footer{height:56px;border-top:1px solid #d9e2ea;display:flex;align-items:center;justify-content:flex-end;gap:8px;padding:0 24px}.modal>footer .ghost{background:#e8eef4;color:#3d4b57}.modal>footer .primary{background:#2f7bea;color:#fff}@media(max-width:1100px){.content-grid{grid-template-columns:1fr}.side-panel{display:none}.toolbar{height:auto;flex-wrap:wrap}.filter{margin-left:0}.summary-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:760px){.summary-grid,.form-layout,.action-layout,.crm-summary{grid-template-columns:1fr}.modal{height:calc(100vh - 20px)}}
</style>
